import { useState, useEffect } from 'react';
import { getCallVoiceById, updateCallVoice } from '../../services/callVoiceService';
import type { CallVoice } from '../../types/callVoice';

interface ChangeVoiceNameModalProps {
  callVoiceId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function ChangeVoiceNameModal({ callVoiceId, onClose, onSuccess }: ChangeVoiceNameModalProps) {
  const [voice, setVoice] = useState<CallVoice | null>(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getCallVoiceById(callVoiceId)
      .then((result) => {
        setVoice(result);
        setName(result.name || '');
      })
      .catch(() => setError('Failed to load voice'))
      .finally(() => setLoading(false));
  }, [callVoiceId]);

  const handleSave = async () => {
    if (!voice) return;
    setSaving(true);
    try {
      await updateCallVoice(voice.id, { ...voice, name });
      onSuccess();
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Change Voice Name</h2>
          {loading && <p className="text-sm text-gray-400">Loading...</p>}
          {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
          {!loading && voice && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Voice Id</label>
                <p className="text-sm text-gray-900">{voice.voiceId}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">Retell Voice Name</label>
                <p className="text-sm text-gray-900">{voice.voiceName}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading || !voice}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: 'var(--ion-color-primary)' }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
